import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { FleetSection } from '../../../../shared/sso-workspace/fleet-page'
import { FleetGrid } from '../../../../shared/sso-workspace/fleet-grid'
import { Drawer } from '../../../../shared/drawer/drawer'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { TableAgInfinite } from '../../../../shared/table-builder/table-ag-infinite'
import { ColumnFormat, ColumnMenu, ColumnText } from '../../../../shared/table-builder/table-columns'
import { Menu, MenuDetails } from '../../../../shared/menu/menu'
import { OrgPicker } from '../../../../shared/org-search/org-picker'
import { IconNotification } from '../../../../shared/icon/icon'
import { EmptyState } from '../../../../shared/empty-state/empty-state'
import { listCaptures } from '../../../../api-new/state/notify'
import { channelLabel, providerLabel } from '../../../../shared/notify/words'
import { agoCell, channelCell, organizationCell, providerCell } from './cells'
import { CaptureDetail, MODE_LABEL, bytesText } from './capture-detail'
import { useCursorGrid } from './use-cursor-grid'
import { CAPTURE_PARAM } from './paths'

const ALL_ORGANIZATIONS = { value: '', label: 'Every organization' }
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const CAPTURE_PROVIDERS = ['sendgrid', 'apns', 'fcm', 'webpush']

// The grid's search box: a trace id or a login id.
export const captureSearch = text => {
  const v = String(text || '').trim()
  if (UUID.test(v)) return { notificationId: v.toLowerCase() }
  if (/^\d{1,18}$/.test(v)) return { loginId: v }
  return {}
}

const captureOf = search => {
  const v = new URLSearchParams(search || '').get(CAPTURE_PARAM)
  return v && /^[1-9]\d{0,17}$/.test(v) ? Number(v) : null
}

const withCapture = (search, id) => {
  const params = new URLSearchParams(search || '')
  if (id) params.set(CAPTURE_PARAM, String(id))
  else params.delete(CAPTURE_PARAM)
  const out = `${params}`
  return out ? `?${out}` : ''
}

const modeCell = row => (row.data ? MODE_LABEL[row.value] || row.value : '')
const sizeCell = row => (row.data ? bytesText(row.value) : '')

// The captured-payload viewer (spec sections 4.6, 13.1; system only): on
// local, dev and CI every push (and, teed, every email) is captured instead
// of, or as well as, being sent, so "what would have gone out, to where"
// can be read without any certificates. Opening one opens its panel over
// the list; the URL names it (?capture=<id>), so a link from a timeline
// works and Back closes it.
export const CapturesTab = () => {
  const history = useHistory()
  const location = useLocation()
  const openID = captureOf(location.search)
  const [filters, setFilters] = useState({ organizationId: '', provider: '', channel: '' })
  const [title, setTitle] = useState('Capture')
  const tableRef = useRef()

  const key = JSON.stringify(filters)
  const grid = useCursorGrid({
    key,
    load: ({ limit, cursor, search }) => listCaptures({ limit, cursor, ...filters, ...captureSearch(search) }),
    failed: "Captured payloads couldn't be loaded."
  })

  const set = (k, v) => setFilters(f => ({ ...f, [k]: v }))
  // Production never captures (notify: 404 notify.capture_disabled).
  const disabled = Boolean(grid.failure && grid.failure.data && grid.failure.data.code === 'notify.capture_disabled')

  const open = useCallback(row => {
    if (!row || !row.id) return
    const here = history.location
    if (captureOf(here.search) === row.id) return
    history.push({ pathname: here.pathname, search: withCapture(here.search, row.id), state: { captureList: true } })
  }, [history])
  const close = () => {
    if (location.state && location.state.captureList) history.goBack()
    else history.replace({ pathname: location.pathname, search: withCapture(location.search, null) })
  }

  const rowMenu = useMemo(() => r => <Menu small><MenuDetails text="details" pin show onClick={() => open(r)} /></Menu>, [open])

  return (
    <FleetSection id="nf-captures" title="Captured payloads"
      lede="What each provider would have received, to where, on environments without real push credentials (and every email when capture is teed). Search by trace id or login id.">
      {disabled &&
        <EmptyState Icon={IconNotification} title="Captures are off on this server"
          message="This is production: the real providers send, and nothing is captured. Captured payloads are kept on local, dev and test environments only." />}
      {grid.error && !disabled && <p className="text-danger small" role="alert">{grid.error}</p>}
      {grid.error && !disabled && <ErrorReference error={grid.failure} className="mb-2" />}
      {!disabled && <>
      <div className="nf-toolbar">
        <span className="nf-org">
          <OrgPicker id="nf-cap-org" label="Organization" value={filters.organizationId} onChange={v => set('organizationId', v)} allOption={ALL_ORGANIZATIONS} small />
        </span>
        <select className="form-select form-select-sm" aria-label="Provider" value={filters.provider} onChange={e => set('provider', e.target.value)}>
          <option value="">Every provider</option>
          {CAPTURE_PROVIDERS.map(p => <option key={p} value={p}>{providerLabel(p)}</option>)}
        </select>
        <select className="form-select form-select-sm" aria-label="Channel" value={filters.channel} onChange={e => set('channel', e.target.value)}>
          <option value="">Every channel</option>
          {['email', 'push'].map(c => <option key={c} value={c}>{channelLabel(c)}</option>)}
        </select>
      </div>
      <FleetGrid title="Captured payloads" tableRef={tableRef} exportName="notify-captures" flush>
        <TableAgInfinite key={key} ref={tableRef} queryKey="captures" onGetRows={grid.getRows} onRowClicked={open}
          emptyState={{ Icon: IconNotification, title: 'Nothing captured', message: 'Nothing has been captured here. On production, capture is off and real providers send.' }}>
          <ColumnFormat field="createdAt" header="When" render={agoCell} sortable={false} />
          <ColumnFormat field="provider" header="Provider" render={providerCell} sortable={false} fit="content" />
          <ColumnFormat field="channel" header="Channel" render={channelCell} sortable={false} fit="content" />
          <ColumnText field="addressRef" header="To" sortable={false} />
          <ColumnFormat field="organizationId" header="Organization" render={organizationCell} sortable={false} />
          <ColumnFormat field="mode" header="Mode" render={modeCell} sortable={false} fit="content" />
          <ColumnFormat field="requestBytes" header="Size" render={sizeCell} sortable={false} fit="content" />
          <ColumnMenu menu={rowMenu} />
        </TableAgInfinite>
      </FleetGrid>
      </>}
      {openID &&
        <Drawer open title={title} label={title} onClose={close} size="lg" phone="full">
          <CaptureDetail key={openID} id={openID} onLoaded={c => setTitle(`${providerLabel(c.provider)} capture ${c.id}`)} />
        </Drawer>}
    </FleetSection>
  )
}
